import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { leadsApi, type Lead } from "@/api/leads";
import { useNavigate } from "react-router-dom";

export default function LeadNotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["lead-followups-upcoming"],
    queryFn: async () => {
      const res = await leadsApi.getUpcomingFollowUps();
      return res.data.data as Lead[];
    },
    refetchInterval: 5 * 60 * 1000,
  });

  const count = data?.length ?? 0;
  const hasAny = count > 0;

  const autoOpenKey = useMemo(() => `thms:crm:upcoming-followups:autoopen:v1`, []);

  useEffect(() => {
    if (!hasAny) return;
    // Auto-open once per 6 hours per browser to ensure user notices follow-ups.
    const now = Date.now();
    const last = Number(localStorage.getItem(autoOpenKey) || "0") || 0;
    if (now - last < 6 * 60 * 60 * 1000) return;
    localStorage.setItem(autoOpenKey, String(now));
    setOpen(true);
  }, [hasAny, autoOpenKey]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-white hover:bg-white/10"
          aria-label="Follow-up reminders"
        >
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b font-medium text-sm">Upcoming follow-ups (7 days)</div>
        <div className="max-h-72 overflow-y-auto">
          {isLoading ? (
            <p className="p-3 text-sm text-muted-foreground">Loading…</p>
          ) : !data || data.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">No upcoming follow-ups.</p>
          ) : (
            <ul className="divide-y">
              {data.map((l) => (
                <li key={l.id} className="p-2 px-3 hover:bg-muted/50">
                  <button
                    type="button"
                    className="text-left w-full text-sm"
                    onClick={() => {
                      setOpen(false);
                      navigate(`/sales-person/leads?openLeadId=${encodeURIComponent(l.id)}`);
                    }}
                  >
                    <div className="font-medium">{l.patientName}</div>
                    <div className="text-xs text-muted-foreground">
                      {l.nextFollowUpAt
                        ? new Date(l.nextFollowUpAt).toLocaleString("en-IN")
                        : "—"}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-2 border-t">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => {
              setOpen(false);
              navigate("/sales-person/leads");
            }}
          >
            Open Leads
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
